import React, { useState, useEffect } from "react"
import { Modal, Form, Select, InputNumber, Input, Row, Col, Card, Alert, Space, message, Tag } from "antd"
import { SwapOutlined, WarningOutlined, CheckCircleOutlined } from "@ant-design/icons"
import { useCompany } from "../../contexts/CompanyContext"
import { useConfig } from "../../contexts/ConfigContext"
import { useRegister } from "../../contexts/RegisterContext"
import { useStore } from "../../contexts/StoreContext"
import { useSecureRequest } from "../../hooks/useSecureRequest"
import useAuth from "../../hooks/useAuth"
import { useItems, useStoreFetch } from "../../hooks/useInventory"
import { TStorageItemRS } from "../../models/storageModel"

import { useUser } from "../../contexts/UserContext"
import { createId } from "../../utils"

interface ConversionModalProps {
  open: boolean
  initialOriginItem?: TStorageItemRS | null
  onClose: () => void
  onSuccess?: () => void
}

export const ConversionModal: React.FC<ConversionModalProps> = ({ open, initialOriginItem, onClose, onSuccess }) => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const { company } = useCompany()
  const { config } = useConfig()
  const { store } = useStore()
  const { register } = useRegister()
  const { user } = useUser()
  const { securePost } = useSecureRequest()
  const { tokenData } = useAuth.useToken('authCsrfToken', config.session, config.key)

  // Fetch all inventory items and storehouses
  const { data: storeData } = useStoreFetch(tokenData, {
    all: true,
    company: company.current?.uid
  })
  const { data: itemsData } = useItems(tokenData, {
    company: company.current?.uid,
    module: config.module
  })

  const items: TStorageItemRS[] = itemsData?.itemList || register?.registerList || []
  const storehouses: any[] = storeData?.storehouse || store?.list || []

  // Filtrar solo productos físicos inventariables (excluir servicios y activos fijos sin stock)
  const physicalItems: TStorageItemRS[] = items.filter(
    (item) => item.register_type === "PRODUCT" || (!item.register_type && Boolean(item.inventory))
  )

  // Selected state for auto-calculation
  const [selectedOriginId, setSelectedOriginId] = useState<number | null>(null)
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null)
  const [originQty, setOriginQty] = useState<number>(1)
  const [conversionFactor, setConversionFactor] = useState<number>(1)

  // Pre-fill initial origin item if provided
  useEffect(() => {
    if (open && initialOriginItem) {
      setSelectedOriginId(initialOriginItem.id)
      setSelectedTargetId(null)
      setOriginQty(1)
      form.setFieldsValue({ 
        origin_element_id: initialOriginItem.id,
        origin_quantity: 1
      })
    }
    if (!open) {
      setSelectedOriginId(null)
      setSelectedTargetId(null)
      setOriginQty(1)
      setConversionFactor(1)
      form.resetFields()
    }
  }, [open, initialOriginItem?.id])

  /**
   * Helper to merge full item details (from initialOriginItem) with Algolia items.
   */
  const findItemWithDetails = (id: number | null | undefined, fallback?: TStorageItemRS | null): TStorageItemRS | undefined => {
    if (!id) return fallback || undefined
    const fromList = items.find(i => String(i.id) === String(id))

    const isFallbackMatch = String(fallback?.id) === String(id)
    const baseFallback = isFallbackMatch ? fallback : null

    if (!fromList && !baseFallback) return undefined

    const merged = {
      ...(fromList || {}),
      ...(baseFallback || {}),
    } as TStorageItemRS

    const convFactor = baseFallback?.conversion_factor ?? fromList?.conversion_factor
    const parentId = baseFallback?.parent_element_id ?? fromList?.parent_element_id

    if (convFactor !== undefined) merged.conversion_factor = convFactor
    if (parentId !== undefined) merged.parent_element_id = parentId

    return merged
  }

  const originItem = findItemWithDetails(selectedOriginId, initialOriginItem)
  const targetItem = findItemWithDetails(selectedTargetId, null)

  /**
   * Helper to calculate the factor between origin and target elements.
   */
  const calculateFactor = (origin?: TStorageItemRS, target?: TStorageItemRS) => {
    if (!origin) return 1

    const originFactor = origin.conversion_factor ? Number(origin.conversion_factor) : 1
    const targetFactor = target?.conversion_factor ? Number(target?.conversion_factor) : 1

    if (originFactor > 1) return originFactor
    if (targetFactor > 1) return targetFactor

    return 1
  }

  /**
   * Resolves the target product bidirectionally.
   */
  const resolveTargetItem = (origin: TStorageItemRS) => {
    let resolvedTarget: TStorageItemRS | undefined = undefined

    // Direction A: Origin points to contained unit (Six Pack -> Cerveza)
    if (origin.parent_element_id) {
      resolvedTarget = items.find(i => String(i.id) === String(origin.parent_element_id))
    }

    // Direction B: Contained unit points to origin (Cerveza -> Six Pack)
    if (!resolvedTarget && items.length > 0) {
      resolvedTarget = items.find(i => String(i.parent_element_id) === String(origin.id))
    }

    return resolvedTarget
  }

  // Auto-detect target item and factor when modal opens, origin changes, or items load
  useEffect(() => {
    if (!open || !originItem) return

    const autoTarget = resolveTargetItem(originItem)
    const effectiveTarget = targetItem || autoTarget

    if (autoTarget && !selectedTargetId) {
      setSelectedTargetId(autoTarget.id)
    }

    const factor = calculateFactor(originItem, effectiveTarget)
    setConversionFactor(factor)

    const qty = originQty || 1
    const expectedTotal = qty * factor

    const targetIdToSet = effectiveTarget?.id || autoTarget?.id

    setTimeout(() => {
      form.setFieldsValue({
        origin_element_id: originItem.id,
        target_element_id: targetIdToSet,
        origin_quantity: qty,
        healthy_quantity: expectedTotal,
        damaged_quantity: 0
      })
    }, 50)
  }, [open, selectedOriginId, selectedTargetId, items.length, initialOriginItem?.id, initialOriginItem?.parent_element_id, initialOriginItem?.conversion_factor])

  // Recalculate default healthy qty when origin qty changes
  const handleOriginQtyChange = (val: number | null) => {
    const qty = val || 1
    setOriginQty(qty)
    const factor = calculateFactor(originItem, targetItem)
    setConversionFactor(factor)
    form.setFieldsValue({
      healthy_quantity: qty * factor,
      damaged_quantity: 0
    })
  }

  // Recalculate when user manually selects a target item
  const handleTargetChange = (targetId: number) => {
    setSelectedTargetId(targetId)
    const selectedTarget = findItemWithDetails(targetId, null)
    const factor = calculateFactor(originItem, selectedTarget)
    setConversionFactor(factor)
    const qty = originQty || 1
    form.setFieldsValue({
      target_element_id: targetId,
      healthy_quantity: qty * factor,
      damaged_quantity: 0
    })
  }

  const selectedStorehouseId = Form.useWatch('storehouse_id', form)
  const formOriginQty = Form.useWatch('origin_quantity', form) || 1
  const formHealthyQty = Form.useWatch('healthy_quantity', form)
  const formDamagedQty = Form.useWatch('damaged_quantity', form)

  const effectiveFactor = calculateFactor(originItem, targetItem)
  const expectedTotalUnits = (Number(formOriginQty) || 1) * effectiveFactor
  const currentHealthyUnits = Number(formHealthyQty ?? 0)
  const currentDamagedUnits = Number(formDamagedQty ?? 0)
  const currentTotalUnits = currentHealthyUnits + currentDamagedUnits
  const isTotalMatching = currentTotalUnits === expectedTotalUnits

  const getOriginAvailableStock = () => {
    if (!originItem || !selectedStorehouseId) return null
    const inv = originItem.inventory?.find((i: any) => String(i.id) === String(selectedStorehouseId) || String(i.uid) === String(selectedStorehouseId))
    if (inv !== undefined && inv !== null) return Number(inv.quantity || 0)
    return Number(originItem.quantity || 0)
  }
  const originStock = getOriginAvailableStock()

  const handleFinish = async (values: any) => {
    try {
      setLoading(true)

      const storehouseId = values.storehouse_id
      const origin = findItemWithDetails(values.origin_element_id, initialOriginItem)
      const target = findItemWithDetails(values.target_element_id, null)

      if (!origin || !target) {
        message.error("Debe seleccionar productos válidos para el fraccionamiento.")
        setLoading(false)
        return
      }

      const healthyQty = Number(values.healthy_quantity || 0)
      const damagedQty = Number(values.damaged_quantity || 0)
      const inputOriginQty = Number(values.origin_quantity || 1)

      if (healthyQty <= 0 && damagedQty <= 0) {
        message.error("Debe especificar al menos 1 unidad resultante (sana o mermada).")
        setLoading(false)
        return
      }

      // Stock validation
      const inv = origin.inventory?.find((i: any) => String(i.id) === String(storehouseId) || String(i.uid) === String(storehouseId))
      const availableStock = inv !== undefined && inv !== null ? Number(inv.quantity || 0) : Number(origin.quantity || 0)

      let isStockOverride = false
      let overrideReason: string | null = null

      if (availableStock < inputOriginQty) {
        const canAuthorize = user.permissions.actions?.includes('storage.action.movement.stock_override') ||
                              user.roles?.some((r: any) => ['SUPER_ADMIN', 'ADMIN_COMPANY', 'STORAGE_ADMIN'].includes(r.role_name) || ['SUPER_ADMIN', 'ADMIN_COMPANY', 'STORAGE_ADMIN'].includes(r.name))
        if (!canAuthorize) {
          message.error(`Stock insuficiente del producto origen (${origin.name}) en la bodega seleccionada. Disponible: ${availableStock}, solicitado: ${inputOriginQty}.`)
          setLoading(false)
          return
        }
        const reason = window.prompt(
          `Inventario insuficiente del producto origen "${origin.name}" (Disponible: ${availableStock}, Solicitado: ${inputOriginQty}).\n\nPor favor ingrese el motivo para autorizar esta conversión sin inventario suficiente (OBLIGATORIO):`
        )
        if (!reason?.trim()) {
          message.error("Operación cancelada: Se requiere ingresar el motivo para autorizar la conversión sin inventario.")
          setLoading(false)
          return
        }
        isStockOverride = true
        overrideReason = reason.trim()
      }

      // Expected total units validation based on conversion factor
      const factor = calculateFactor(origin, target)
      const expectedTotal = inputOriginQty * factor
      const actualTotal = healthyQty + damagedQty

      if (actualTotal !== expectedTotal) {
        message.error(`La suma de unidades sanas (${healthyQty}) y mermadas (${damagedQty}) es de ${actualTotal}, pero debe ser exactamente igual a las ${expectedTotal} unidades esperadas de la fracción (${inputOriginQty} x ${factor}).`)
        setLoading(false)
        return
      }

      const payload = {
        origin,
        target,
        storehouseId,
        inputOriginQty,
        healthyQty,
        damagedQty,
        notes: values.notes || "",
        isStockOverride,
        overrideReason,
        user: {
          id: user.id || null,
          name: user.name || "",
          document_number: user.document_number || ""
        }
      }

      const response = await securePost('/inventory/movement/process-conversion', {
        company: company.current?.uid,
        company_id: company.current?.id,
        conversion: payload
      })

      if (response && (response.status === 200 || response.conversionDocNumber)) {
        const wasteInfo = response.wasteDocNumber ? ` | Merma: ${response.wasteDocNumber}` : ''
        message.success(`Fraccionamiento atómico procesado (${response.conversionDocNumber || 'FRAC'}${wasteInfo}).`)
        form.resetFields()
        onClose()
        if (onSuccess) onSuccess()
      } else {
        message.error(response?.message || "Error al procesar el fraccionamiento.")
      }
    } catch (err: any) {
      message.error(err?.message || "Ocurrió un error inesperado al procesar la conversión.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <Modal
      open={open}
      title={
        <Space>
          <SwapOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
          <span>Fraccionamiento y Conversión de Presentación</span>
        </Space>
      }
      onCancel={onClose}
      onOk={() => form.submit()}
      confirmLoading={loading}
      okText="Procesar Fraccionamiento"
      cancelText="Cancelar"
      width={700}
      destroyOnClose
    >
      <Alert
        message="Ajuste de Presentación e Inventario"
        description="Al fraccionar una presentación mayor (ej. Caja), el sistema descontará la cantidad del empaque origen y acreditará las unidades sanas resultantes al stock. Las unidades mermadas serán registradas como pérdidas por daño."
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={{ origin_quantity: 1, damaged_quantity: 0 }}
      >
        <Row gutter={16}>
          <Col span={24}>
            <Form.Item
              name="storehouse_id"
              label="Bodega / Almacén"
              rules={[{ required: true, message: 'Seleccione la bodega' }]}
            >
              <Select placeholder="Seleccione la bodega donde se realiza el fraccionamiento">
                {storehouses.map((st: any) => (
                  <Select.Option key={st.id || st.value} value={st.id || st.value}>
                    {st.name || st.alias || st.label}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Card title="1. Presentación Origen (Salida de Almacén)" size="small" style={{ marginBottom: 16, borderColor: '#ff4d4f' }}>
          {originStock !== null && (
            <Alert
              message={`Stock disponible en bodega seleccionada: ${originStock} unidades`}
              type={originStock >= (originQty || 1) ? "info" : "error"}
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}
          <Row gutter={16}>
            <Col span={16}>
              <Form.Item
                name="origin_element_id"
                label="Producto / Empaque Mayor"
                rules={[{ required: true, message: 'Seleccione el producto origen' }]}
              >
                <Select
                  showSearch
                  disabled={!!initialOriginItem}
                  placeholder="Buscar producto origen (ej. Caja x6, Display)..."
                  optionLabelProp="label"
                  popupMatchSelectWidth={false}
                  styles={{ popup: { root: { minWidth: 420, maxWidth: 600 } } }}
                  filterOption={(input, option) => {
                    const item = physicalItems.find((i: any) => i.id === option?.value);
                    if (!item) return false;
                    const searchLower = input.toLowerCase();
                    const nameMatch = (item.name || '').toLowerCase().includes(searchLower);
                    const skuMatch = (item.sku || '').toLowerCase().includes(searchLower);
                    const customIdMatch = (item.custom_id || '').toLowerCase().includes(searchLower);
                    const barcodeMatch = (item.barcode || '').toLowerCase().includes(searchLower);
                    const categoryMatch = (item.category || '').toLowerCase().includes(searchLower);
                    return nameMatch || skuMatch || customIdMatch || barcodeMatch || categoryMatch;
                  }}
                  onChange={(val) => setSelectedOriginId(val)}
                >
                  {physicalItems.map((item) => {
                    const storeStock = selectedStorehouseId
                      ? (item.inventory?.find((inv: any) => String(inv.id) === String(selectedStorehouseId) || String(inv.uid) === String(selectedStorehouseId))?.quantity ?? 0)
                      : (item.inventory?.reduce((sum: number, inv: any) => sum + (inv.quantity || 0), 0) ?? item.quantity ?? 0);
                    const code = item.custom_id || item.sku;
                    const umName = register.umOptions?.find((u: any) => u.key === item.measure_unit)?.string_value || item.measure_unit || 'UND';
                    const categoryName = register.categoryOptions?.find((c: any) => c.key === item.category)?.string_value || item.category;
                    const factor = item.conversion_factor ? Number(item.conversion_factor) : 1;
                    const labelText = `${item.name}${code ? ` (${code})` : ''} - ${umName}`;

                    return (
                      <Select.Option key={item.id} value={item.id} label={labelText} style={{ padding: '6px 12px', height: 'auto' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', whiteSpace: 'normal' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                            <span style={{ fontWeight: 600, color: '#262626', fontSize: '13px', lineHeight: 1.3 }}>
                              {item.name}
                            </span>
                            <Space size={4} style={{ flexShrink: 0 }}>
                              {factor > 1 && (
                                <Tag color="purple" style={{ margin: 0, fontSize: '11px', lineHeight: '18px', padding: '0 6px' }}>
                                  Contiene x{factor}
                                </Tag>
                              )}
                              <Tag color="blue" style={{ margin: 0, fontSize: '11px', lineHeight: '18px', padding: '0 6px' }}>
                                {umName}
                              </Tag>
                            </Space>
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#8c8c8c' }}>
                            <Space size="small">
                              {code && <span>Código: <strong>{code}</strong></span>}
                              {categoryName && <span>• {categoryName}</span>}
                            </Space>
                            <span style={{ color: storeStock > 0 ? '#52c41a' : '#faad14', fontWeight: 500 }}>
                              Stock: {storeStock} {umName}
                            </span>
                          </div>
                        </div>
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                name="origin_quantity"
                label="Cant. a Desglosar"
                rules={[{ required: true, message: 'Ingrese la cantidad' }]}
              >
                <InputNumber
                  min={1}
                  style={{ width: '100%' }}
                  onChange={handleOriginQtyChange}
                />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card title="2. Presentación Destino (Resultado en Unidades)" size="small" style={{ marginBottom: 16, borderColor: '#52c41a' }}>
          {targetItem && (
            <Alert
              message={
                isTotalMatching
                  ? `Total esperado: ${expectedTotalUnits} unidades (${formOriginQty} x factor ${effectiveFactor}). Concurrencia exacta de sanas (${currentHealthyUnits}) y mermadas (${currentDamagedUnits}).`
                  : `Inconsistencia en desglose: La suma actual de sanas + mermadas es ${currentTotalUnits}, pero se esperan exactamente ${expectedTotalUnits} unidades (${formOriginQty} x factor ${effectiveFactor}).`
              }
              type={isTotalMatching ? "success" : "warning"}
              showIcon
              style={{ marginBottom: 12 }}
            />
          )}
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="target_element_id"
                label="Producto Destino (Unidad/Menor)"
                rules={[{ required: true, message: 'Seleccione el producto destino' }]}
              >
                <Select
                  showSearch
                  disabled={!!initialOriginItem && Boolean(targetItem || originItem?.parent_element_id)}
                  placeholder="Buscar producto destino (ej. Unidad)..."
                  optionLabelProp="label"
                  popupMatchSelectWidth={false}
                  styles={{ popup: { root: { minWidth: 420, maxWidth: 600 } } }}
                  filterOption={(input, option) => {
                    const candidateItems = physicalItems.filter(i => String(i.id) !== String(selectedOriginId));
                    const item = candidateItems.find((i: any) => i.id === option?.value);
                    if (!item) return false;
                    const searchLower = input.toLowerCase();
                    const nameMatch = (item.name || '').toLowerCase().includes(searchLower);
                    const skuMatch = (item.sku || '').toLowerCase().includes(searchLower);
                    const customIdMatch = (item.custom_id || '').toLowerCase().includes(searchLower);
                    const barcodeMatch = (item.barcode || '').toLowerCase().includes(searchLower);
                    const categoryMatch = (item.category || '').toLowerCase().includes(searchLower);
                    return nameMatch || skuMatch || customIdMatch || barcodeMatch || categoryMatch;
                  }}
                  onChange={(val) => handleTargetChange(val)}
                >
                  {physicalItems
                    .filter((item) => String(item.id) !== String(selectedOriginId))
                    .map((item) => {
                      const storeStock = selectedStorehouseId
                        ? (item.inventory?.find((inv: any) => String(inv.id) === String(selectedStorehouseId) || String(inv.uid) === String(selectedStorehouseId))?.quantity ?? 0)
                        : (item.inventory?.reduce((sum: number, inv: any) => sum + (inv.quantity || 0), 0) ?? item.quantity ?? 0);
                      const code = item.custom_id || item.sku;
                      const umName = register.umOptions?.find((u: any) => u.key === item.measure_unit)?.string_value || item.measure_unit || 'UND';
                      const categoryName = register.categoryOptions?.find((c: any) => c.key === item.category)?.string_value || item.category;
                      const labelText = `${item.name}${code ? ` (${code})` : ''} - ${umName}`;

                      return (
                        <Select.Option key={item.id} value={item.id} label={labelText} style={{ padding: '6px 12px', height: 'auto' }}>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', whiteSpace: 'normal' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px' }}>
                              <span style={{ fontWeight: 600, color: '#262626', fontSize: '13px', lineHeight: 1.3 }}>
                                {item.name}
                              </span>
                              <Tag color="green" style={{ margin: 0, fontSize: '11px', lineHeight: '18px', padding: '0 6px', flexShrink: 0 }}>
                                {umName}
                              </Tag>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '11px', color: '#8c8c8c' }}>
                              <Space size="small">
                                {code && <span>Código: <strong>{code}</strong></span>}
                                {categoryName && <span>• {categoryName}</span>}
                              </Space>
                              <span style={{ color: storeStock > 0 ? '#52c41a' : '#faad14', fontWeight: 500 }}>
                                Stock: {storeStock} {umName}
                              </span>
                            </div>
                          </div>
                        </Select.Option>
                      );
                    })}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="healthy_quantity"
                label={
                  <span>
                    <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 4 }} />
                    Unidades Sanas (Ingresan a Stock)
                  </span>
                }
                rules={[{ required: true, message: 'Ingrese unidades sanas' }]}
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="damaged_quantity"
                label={
                  <span>
                    <WarningOutlined style={{ color: '#ff4d4f', marginRight: 4 }} />
                    Unidades Dañadas / Merma
                  </span>
                }
              >
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Form.Item
          name="notes"
          label="Observaciones / Motivo de Daño"
        >
          <Input.TextArea
            rows={2}
            placeholder="Ej: Empaque mojado durante descarga, 1 unidad rota. Se recuperan 5 unidades sanas para mostrador."
          />
        </Form.Item>
      </Form>
    </Modal>
  )
}

export default ConversionModal
